import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

export default function ChangePassword() {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === false) { nav("/login"); return null; }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      await refreshUser();
      nav("/");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="max-w-md w-full border border-border bg-card rounded-sm p-8 hard-shadow">
        <div className="h-11 w-11 bg-primary text-primary-foreground flex items-center justify-center rounded-sm mb-5">
          <KeyRound className="h-5 w-5" />
        </div>
        <h1 className="text-2xl font-display font-black tracking-tight">Set a new password</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          For security, please change the default password before continuing.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">Current password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required
              className="mt-1.5 rounded-sm h-11" data-testid="cp-current-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required
              className="mt-1.5 rounded-sm h-11" data-testid="cp-new-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
              className="mt-1.5 rounded-sm h-11" data-testid="cp-confirm-input" />
          </div>
          {error && <p className="text-sm text-destructive font-medium" data-testid="cp-error">{error}</p>}
          <Button type="submit" disabled={loading}
            className="w-full h-11 rounded-sm font-bold uppercase tracking-wide gap-2" data-testid="cp-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
          <button type="button" onClick={async () => { await logout(); nav("/login"); }}
            className="text-xs text-muted-foreground hover:text-foreground w-full text-center">
            Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
}
